import React, { useEffect, useRef, useState } from "react";
import { Checkbox, Menu, Dropdown, Button, Modal, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import Result from "./components/Result";
import Search from "./components/Search";
import { useDispatch, useSelector } from "react-redux";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import {
  hideCounter,
  hideCounters,
  axisAlignCounter,
  removeCounter,
  updateCounterChartType,
  addCounter,
  clearCounters,
} from "src/features/performanceManagement/pmSlice";
import { pickTextColorBasedOnBgColor } from "@helpers/utils";
import SeriesColorPicker from "./components/SeriesColorPicker";
import { fetchWorkspaceAsync } from "src/features/performanceManagement/workspaceSlice";
import workspaceService from "@services/workspace.service";

const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";

const { SubMenu } = Menu;
const { confirm } = Modal;

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const menu = (workspaces, setSelectedTab, removeFromWorkspace) => (
  <Menu>
    {workspaces.map((workspace, i) => {
      return (
        <Menu.Item key={i} className="p-0">
          <div className="flex justify-between items-center">
            <span
              onClick={() => setSelectedTab(workspace._id)}
              className="p-3 py-1 flex-grow"
            >
              {workspace.name}
            </span>

            {workspace._id && (
              <div
                className="pr-3 py-1"
                onClick={() =>
                  removeFromWorkspace(workspace._id, workspace.name)
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>
        </Menu.Item>
      );
    })}
  </Menu>
);

function Queries() {
  const [expanded, setExpanded] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null);

  const dispatch = useDispatch();
  const { data: counters } = useSelector((state) => state.pms);
  const { data: workspaces, status } = useSelector((state) => state.workspace);

  const closeSearchArea = () => {
    setExpanded(false);
  };

  useEffect(() => {
    dispatch(fetchWorkspaceAsync());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(clearCounters());
    if (selectedTab) {
      try {
        const { kpi_query_list } = workspaces.find(
          (x) => x._id === selectedTab
        );
        const queries = JSON.parse(kpi_query_list);
        queries.map((query) => dispatch(addCounter(query)));
      } catch (e) {
        console.log(e);
      }
    }
    // eslint-disable-next-line
  }, [selectedTab]);

  const handleDelete = async (id) => {
    try {
      await workspaceService.deleteWorkspace(id);
      setTimeout(() => {
        dispatch(fetchWorkspaceAsync());
      }, 1000);
    } catch (err) {
      message.error("action has error deleting Workspace");
      console.log(err);
    }
  };

  const removeFromWorkspace = (id, name) => {
    confirm({
      title: `Delete '${name}'`,
      icon: <ExclamationCircleOutlined />,
      content: "Do you want to delete this item?",
      onOk() {
        handleDelete(id);
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  return (
    <div className="flex pt-16 relative flex-col flex-grow h-full">
      <div
        className={`${
          expanded ? "h-full" : "h-16"
        } transition-all duration-300 absolute top-0 z-20 w-full flex flex-col bg-white border-b border-gray-200 overflow-hidden`}
      >
        <div className="h-16 flex-shrink-0 flex items-center p-5 space-x-3">
          <Button
            type="primary"
            htmlType="button"
            className="p-2 flex-shrink-0"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </Button>

          <div
            className="flex-grow flex items-center"
            onClick={() => setExpanded(true)}
          >
            <span className="text-gray-400">Search...</span>
          </div>
        </div>
        <div
          className="flex-grow overflow-auto flex"
          style={{ backgroundColor: "#f6f6f6" }}
        >
          <Search close={closeSearchArea} />
        </div>
      </div>

      <div className="flex flex-grow relative w-full">
        <div className="w-72 flex-shrink-0 bg-white border-r border-gray-200 overflow-auto">
          <div className="pt-5 px-5 flex items-center">
            <span className="text-gray-750 border-b border-dashed border-gray-700 mr-1">
              Select
            </span>
            <span className="text-gray-750 mr-1">one of</span>
            <Dropdown
              overlay={menu(
                [
                  { name: "New workspace", kpi_query_list: [], _id: "" },
                  ...workspaces,
                ],
                setSelectedTab,
                removeFromWorkspace
              )}
              trigger={["click"]}
            >
              <div className="cursor-pointer font-medium flex items-center">
                <span className="">
                  {selectedTab
                    ? workspaces.find((x) => x._id === selectedTab).name ||
                      "Workspace"
                    : "Workspace"}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 ml-1 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </Dropdown>
          </div>

          <div
            className={classNames(
              "bg-white rounded-xl p-3",
              "focus:outline-none"
            )}
          >
            <ul className="relative">
              {!counters.length && (
                <div className="flex items-center px-2 space-x-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                  <span>No Data, please the first Search.</span>
                </div>
              )}
              {counters.map((counter, i) => (
                <Item key={i} counter={counter} />
              ))}
            </ul>
          </div>
        </div>
        <div
          className="flex p-5 w-full"
          style={{ maxWidth: "calc(100vw - 33rem)" }}
        >
          <Result />
        </div>
      </div>
    </div>
  );
}

export default Queries;

const dates = [
  "Last Day",
  "Last 7 Days",
  "Last 2 Weeks",
  "Last 3 Weeks",
  "Last 30 Days",
];

const Item = ({ counter }) => {
  const { data: kpis } = useSelector((state) => state.kpis);

  const dispatch = useDispatch();

  const ref = useRef();

  function exportData(counter) {
    const kpiName =
      kpis.findIndex((x) => x._id === counter.kpi_id) > -1
        ? kpis.find((x) => x._id === counter.kpi_id).name || counter.kpi_id
        : counter.kpi_id;

    const filename = `${kpiName} - ${counter.buket} - ${counter.value} (${
      dates.includes(counter.startDate)
        ? `${counter.startDate}`
        : `${counter.startDate} -> ${counter.endDate}`
    })`;

    const ws = XLSX.utils.json_to_sheet(counter.data);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, filename + fileExtension);
  }

  const menuQuery = (item, exportData) => (
    <Menu className="w-36">
      <SubMenu
        title={
          <div className="flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              />
            </svg>
            <span className="" onClick={(e) => e.preventDefault()}>
              Color
            </span>
          </div>
        }
        // className="w-32"
        key="menu-color"
      >
        <Menu.Item key="color">
          <SeriesColorPicker id={item.id} />
        </Menu.Item>
      </SubMenu>
      <SubMenu
        title={
          <div className="flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
              />
            </svg>
            <span className="" onClick={(e) => e.preventDefault()}>
              Chart type
            </span>
          </div>
        }
        // className="w-32"
        key="menu-chart-type"
      >
        <Menu.Item
          key="line"
          onClick={() =>
            dispatch(updateCounterChartType({ id: item.id, type: "line" }))
          }
        >
          Line
        </Menu.Item>
        <Menu.Item
          key="spline"
          onClick={() =>
            dispatch(updateCounterChartType({ id: item.id, type: "spline" }))
          }
        >
          Spline
        </Menu.Item>
        <Menu.Item
          key="column"
          onClick={() =>
            dispatch(updateCounterChartType({ id: item.id, type: "column" }))
          }
        >
          Bar
        </Menu.Item>
      </SubMenu>
      <SubMenu
        title={
          <div className="flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h8m-8 6h16"
              />
            </svg>

            <span className="" onClick={(e) => e.preventDefault()}>
              Y-Axis
            </span>
          </div>
        }
        key="menu-y-axis"
      >
        <Menu.Item key="menu-1-1">
          <span
            className=""
            onClick={(e) =>
              dispatch(
                axisAlignCounter({ id: item.id, type: "y", align: "left1" })
              )
            }
          >
            Left 1
          </span>
        </Menu.Item>
        <Menu.Item key="menu-1-2">
          <span
            className=""
            onClick={(e) =>
              dispatch(
                axisAlignCounter({ id: item.id, type: "y", align: "left2" })
              )
            }
          >
            Left 2
          </span>
        </Menu.Item>
        <Menu.Item key="menu-1-3">
          <span
            className=""
            onClick={(e) =>
              dispatch(
                axisAlignCounter({ id: item.id, type: "y", align: "right1" })
              )
            }
          >
            Right 1
          </span>
        </Menu.Item>
        <Menu.Item key="menu-1-4">
          <span
            className=""
            onClick={(e) =>
              dispatch(
                axisAlignCounter({ id: item.id, type: "y", align: "right2" })
              )
            }
          >
            Right 2
          </span>
        </Menu.Item>
      </SubMenu>
      <SubMenu
        title={
          <div className="flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h8m-8 6h16"
              />
            </svg>

            <span className="" onClick={(e) => e.preventDefault()}>
              X-Axis
            </span>
          </div>
        }
        key="menu-x-axis"
      >
        <Menu.Item key="x-axis-1">
          <span
            className=""
            onClick={(e) =>
              dispatch(
                axisAlignCounter({ id: item.id, type: "x", align: "bottom1" })
              )
            }
          >
            Bottom 1
          </span>
        </Menu.Item>
        <Menu.Item key="x-axis-2">
          <span
            className=""
            onClick={(e) =>
              dispatch(
                axisAlignCounter({ id: item.id, type: "x", align: "bottom2" })
              )
            }
          >
            Bottom 2
          </span>
        </Menu.Item>
      </SubMenu>
      <Menu.Item key="menu-export">
        <div
          className="flex items-center space-x-2"
          onClick={(e) => exportData(item)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
            />
          </svg>

          <span className="">Export</span>
        </div>
      </Menu.Item>
      <Menu.Item key="menu-hide">
        <div
          className="flex items-center space-x-2"
          onClick={(e) => dispatch(hideCounter(item.id))}
        >
          {item.show ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          )}
          <span className="">{item.show ? "Hide" : "Show"}</span>
        </div>
      </Menu.Item>
      <Menu.Item key="menu-hide-conuter">
        <div
          className="flex items-center space-x-2"
          onClick={(e) => dispatch(hideCounters(item.id))}
        >
          {item.counterShow ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          )}
          <span className="">
            {item.counterShow ? "Hide" : "Show"} Counters
          </span>
        </div>
      </Menu.Item>
      <Menu.Item key="menu-delete">
        <div
          className="flex items-center space-x-2"
          onClick={(e) => dispatch(removeCounter(item.id))}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          <span className="">Delete</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <li
        key={counter.id}
        className="relative p-3 space-x-3 rounded-md hover:bg-coolGray-100 flex"
        style={{ minHeight: "5.5rem" }}
        ref={ref}
      >
        <div className="flex items-start py-0.5">
          <Checkbox />
        </div>
        <div className="flex-grow" style={{ minWidth: 0 }}>
          <span className="font-medium break-all">
            {(kpis.findIndex((x) => x._id === counter.kpi_id) > -1 ? kpis.find((x) => x._id === counter.kpi_id).name ||
                counter.kpi_id : counter.kpi_id) +` - ${counter.buket} - ${counter.value} (${
                dates.includes(counter.startDate)
                  ? `${counter.startDate}`
                  : `${counter.startDate} -> ${counter.endDate}`
              })`}
          </span>

          <ul className="flex mt-1 space-x-1 text-xs font-normal leading-4 text-coolGray-500">
            <li>{counter.date}</li>
          </ul>
        </div>
        <div className="flex flex-col justify-between items-end py-0.5">
          <div className="bg-white rounded-full">
            <Dropdown
              overlay={menuQuery(counter, exportData)}
              trigger={["click"]}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </Dropdown>
          </div>

          <div
            className="w-5 h-5 flex justify-center items-center rounded-full p-1"
            style={{
              backgroundColor: counter.color,
              color: pickTextColorBasedOnBgColor(counter.color),
            }}
          >
            <small className="">
              {`${(counter.align || { y: " " }).y.split("")[0]}${
                (counter.align || { y: " " }).y.split("")[
                  (counter.align || { y: " " }).y.length - 1
                ]
              }`.toUpperCase()}
            </small>
          </div>
        </div>
      </li>
    </>
  );
};
